import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { AuthLayout } from '../layouts/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export const LoginPage = () => {
    const { orgCode } = useParams();
    const { login } = useAuth();
    const { tenant, error: tenantError } = useTenant();
    const navigate = useNavigate();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    if (tenantError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle className="text-red-600">Organisation nicht gefunden</CardTitle>
                        <CardDescription>
                            Der Organisationscode "{orgCode}" existiert nicht.
                        </CardDescription>
                    </CardHeader>
                </Card>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const { user: userData } = await login(email, password, orgCode);
            
            // Check if email is verified
            if (!userData.email_verified && userData.role === 'customer') {
                localStorage.setItem('pending_verification_email', email);
                toast.error('Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse');
                navigate(`/o/${orgCode}/pending-verification`);
                return;
            }
            
            toast.success('Willkommen zurück!');
            
            // Redirect based on role
            if (userData.role === 'org_admin') {
                navigate(`/o/${orgCode}/admin/referrals`);
            } else if (userData.role === 'sales_rep') {
                navigate(`/o/${orgCode}/sales`);
            } else {
                navigate(`/o/${orgCode}/dashboard`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Ungültige Anmeldedaten');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout>
            <Card className="border-0 shadow-lg">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-2xl font-bold">Anmelden</CardTitle>
                    <CardDescription>
                        Geben Sie Ihre Zugangsdaten ein, um auf Ihr Konto zuzugreifen
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">E-Mail</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="ihre.email@beispiel.de"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password">Passwort</Label>
                                <Link 
                                    to={`/o/${orgCode}/forgot-password`}
                                    className="text-sm hover:underline"
                                    style={{ color: tenant?.primary_color }}
                                    data-testid="forgot-password-link"
                                >
                                    Passwort vergessen?
                                </Link>
                            </div>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Geben Sie Ihr Passwort ein"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full btn-tenant-primary"
                            style={{ backgroundColor: tenant?.primary_color }}
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Anmeldung läuft...
                                </>
                            ) : (
                                'Anmelden'
                            )}
                        </Button>
                    </form>
                    
                    <div className="mt-6 text-center text-sm text-slate-600">
                        Sie haben noch kein Konto?{' '}
                        <Link 
                            to={`/o/${orgCode}/register`}
                            className="font-medium hover:underline"
                            style={{ color: tenant?.primary_color }}
                            data-testid="register-link"
                        >
                            Registrieren
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </AuthLayout>
    );
};
